// Les entreprises autour du gymnase (D-111), d'après le registre public : API « Recherche d'entreprises » de
// l'État (recherche-entreprises.api.gouv.fr). Ici, sans réseau ni base : ce qu'on garde d'une réponse, ce qu'on
// exclut, les secteurs, les tailles, la distance et l'affinité. La lecture elle-même : src/lib/registre.ts.
//   * Gardés : nom, activité (NAF) et son libellé, tranche d'effectif, adresse, distance au gymnase, SIRET.
//   * Exclus : établissement ou entreprise fermés, diffusion restreinte, associations et organismes publics,
//     alcool, tabac et jeux d'argent (loi Évin : jamais affichés, D-033), et l'entrepreneur individuel sans
//     enseigne (son nom est celui d'une personne).
//   * Jamais de dirigeant : la lecture ne les demande pas (minimal=true) et rien ici ne les lit.

export type Family = 'batiment' | 'commerce' | 'sante' | 'auto' | 'services' | 'immobilier' | 'autres';
export const FAMILIES: Family[] = ['batiment', 'commerce', 'sante', 'auto', 'services', 'immobilier', 'autres'];
/** Les filtres de la liste (« Tous les secteurs », puis ceux-ci). */
export const FAMILY_LABEL: Record<Family, string> = {
  batiment: 'Artisans du bâtiment', commerce: 'Commerce', sante: 'Santé', auto: 'Auto et transport',
  services: 'Services', immobilier: 'Immobilier', autres: 'Autres',
};

const NAF = /^[0-9]{2}\.[0-9]{2}[A-Z]$/;
export const isNaf = (s: unknown): s is string => typeof s === 'string' && NAF.test(s);

/** Loi Évin (D-033) : alcool, tabac, jeux d'argent ne s'affichent jamais ; inutile de les démarcher. */
export function isEvin(naf: string): boolean {
  return /^11\.0[1-6]/.test(naf) || ['01.21Z', '46.34Z', '47.25Z', '47.26Z', '56.30Z', '92.00Z', '12.00Z', '46.35Z'].includes(naf);
}

export function familyOf(naf: string | null | undefined): Family {
  if (!naf || !isNaf(naf)) return 'autres';
  const d = Number(naf.slice(0, 2));
  if (['47.73Z', '47.74Z', '47.78A', '75.00Z'].includes(naf) || d === 86 || d === 87 || d === 88) return 'sante';
  if (naf === '85.53Z' || naf === '47.30Z' || naf.startsWith('77.1') || d === 45 || (d >= 49 && d <= 53)) return 'auto';
  if (d >= 41 && d <= 43 || ['16.23Z', '81.30Z', '46.73A', '46.73B', '77.32Z', '23.61Z', '25.12Z'].includes(naf)) return 'batiment';
  if (d === 68) return 'immobilier';
  if (d === 47 || d === 55 || d === 56 || d === 95 || /^10\.(1[1-3]|71)/.test(naf) || /^96\.0[24]/.test(naf)) return 'commerce';
  if ((d >= 58 && d <= 66) || (d >= 69 && d <= 82) || d === 85 || d === 93 || d === 96) return 'services';
  return 'autres';
}

/** Le secteur en peu de mots (« Menuiserie bois et PVC ») ; à défaut, le libellé officiel raccourci. */
const SHORT: Record<string, string> = {
  '43.32A': 'Menuiserie bois et PVC', '43.32B': 'Menuiserie métallique', '43.32C': 'Agencement de lieux de vente',
  '43.21A': 'Électricité', '43.22A': 'Plomberie', '43.22B': 'Chauffage et climatisation', '43.31Z': 'Plâtrerie, isolation',
  '43.29A': 'Isolation', '43.33Z': 'Revêtement des sols et des murs', '43.34Z': 'Peinture et vitrerie', '43.91A': 'Charpente',
  '43.91B': 'Couverture', '43.99C': 'Maçonnerie', '43.12A': 'Terrassement', '41.20A': 'Construction de maisons', '81.30Z': 'Paysagiste',
  '46.73A': 'Négoce de matériaux', '16.23Z': 'Charpente et menuiserie',
  '45.20A': 'Garage automobile', '45.20B': 'Carrosserie', '45.11Z': 'Concession automobile', '45.32Z': 'Équipement automobile',
  '45.40Z': 'Motos', '47.30Z': 'Station-service', '49.41A': 'Transport de marchandises', '49.41B': 'Transport de proximité',
  '49.42Z': 'Déménagement', '49.32Z': 'Taxi', '49.39A': 'Transport de voyageurs', '52.29A': 'Messagerie et fret', '77.11A': 'Location de voitures',
  '85.53Z': 'Auto-école',
  '68.31Z': 'Agence immobilière', '68.32A': 'Syndic et gestion', '68.20A': 'Location de logements', '68.20B': 'Location de locaux',
  '10.71C': 'Boulangerie-pâtisserie', '10.71D': 'Pâtisserie', '10.13B': 'Charcuterie', '47.22Z': 'Boucherie', '47.21Z': 'Fruits et légumes',
  '47.23Z': 'Poissonnerie', '47.24Z': 'Boulangerie', '47.29Z': 'Épicerie fine', '47.11B': 'Alimentation générale', '47.11C': 'Supérette',
  '47.11D': 'Supermarché', '47.11F': 'Hypermarché', '47.71Z': 'Vêtements', '47.72A': 'Chaussures', '47.64Z': 'Articles de sport',
  '47.65Z': 'Jeux et jouets', '47.76Z': 'Fleuriste', '47.62Z': 'Presse et papeterie', '47.52A': 'Quincaillerie', '47.59B': 'Équipement de la maison',
  '47.77Z': 'Bijouterie', '47.54Z': 'Électroménager', '56.10A': 'Restaurant', '56.10C': 'Restauration rapide', '56.21Z': 'Traiteur',
  '55.10Z': 'Hôtel', '96.02A': 'Coiffure', '96.02B': 'Institut de beauté', '96.04Z': 'Bien-être',
  '86.90E': 'Kinésithérapie', '86.21Z': 'Médecin généraliste', '86.23Z': 'Dentiste', '86.90D': 'Infirmiers', '86.90A': 'Ambulances',
  '47.73Z': 'Pharmacie', '47.78A': 'Optique', '47.74Z': 'Matériel médical', '75.00Z': 'Vétérinaire',
  '69.20Z': 'Expertise comptable', '69.10Z': 'Juridique', '64.19Z': 'Banque', '65.12Z': 'Assurance', '66.22Z': 'Courtage d’assurances',
  '71.11Z': 'Architecte', '71.12B': 'Bureau d’études', '73.11Z': 'Publicité', '62.01Z': 'Informatique', '78.20Z': 'Travail temporaire',
  '93.13Z': 'Salle de sport', '79.11Z': 'Agence de voyages',
};
export function shortActivity(naf: string | null | undefined, label?: string | null): string {
  if (naf && SHORT[naf]) return SHORT[naf]!;
  let s = (label ?? '').trim();
  if (!s) return FAMILY_LABEL[familyOf(naf)];
  s = s.replace(/ en magasin spécialisé$/, '').replace(/^Commerces? de détail (de la |de l’|de |d’|des )/, '')
    .replace(/^Travaux (de la |de l’|de |d’|des )/, '').replace(/^Activités? (des |de la |de l’|de |d’|du )/, '').split(/[,;(]/)[0]!.trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// --- Tailles (tranches d'effectif de l'INSEE) -------------------------------------------------------
export const SIZE_LABEL: Record<string, string> = {
  NN: 'sans salarié', '00': 'sans salarié', '01': '1 ou 2 salariés', '02': '3 à 5 salariés', '03': '6 à 9 salariés',
  '11': '10 à 19 salariés', '12': '20 à 49 salariés', '21': '50 à 99 salariés', '22': '100 à 199 salariés', '31': '200 à 249 salariés',
  '32': '250 à 499 salariés', '41': '500 à 999 salariés', '42': '1 000 à 1 999 salariés', '51': '2 000 à 4 999 salariés',
  '52': '5 000 à 9 999 salariés', '53': '10 000 salariés et plus',
};
export const sizeLabel = (code: string | null | undefined) => (code && SIZE_LABEL[code]) || 'effectif inconnu';
export type SizeBand = '' | 'zero' | 'tpe' | 'pme' | 'grande';
export const SIZE_BANDS: { key: SizeBand; label: string }[] = [
  { key: '', label: 'toutes' }, { key: 'zero', label: 'sans salarié' }, { key: 'tpe', label: '1 à 9' }, { key: 'pme', label: '10 à 49' }, { key: 'grande', label: '50 et plus' },
];
export function sizeBand(code: string | null | undefined): Exclude<SizeBand, ''> {
  if (!code || code === 'NN' || code === '00') return 'zero';
  if (['01', '02', '03'].includes(code)) return 'tpe';
  if (['11', '12'].includes(code)) return 'pme';
  return 'grande';
}

// --- Distance -------------------------------------------------------------------------------------------
export interface Point { lat: number; lon: number }
/** Distance à vol d'oiseau, en mètres. */
export function distanceM(a: Point, b: Point): number {
  const R = 6_371_000, rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad, dLon = (b.lon - a.lon) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLon / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.min(1, Math.sqrt(h))));
}
/** « 0,6 km », « 1,4 km », « 12 km ». */
export function kmLabel(m: number | null | undefined): string {
  if (m == null) return '';
  const km = m / 1000;
  return km < 10 ? `${(Math.round(km * 10) / 10).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km` : `${Math.round(km)} km`;
}

// --- Affinité : secteur, taille, distance --------------------------------------------------------------
// Barème (expliqué dans l'aide, tuto « prospection ») : jusqu'à 4 points pour le secteur, 3 pour la taille, 3 pour
// la distance ; 8 et plus : forte, 5 à 7 : moyenne, sinon faible.
export const SECTOR_POINTS: Record<Family, number> = { batiment: 4, auto: 4, immobilier: 4, commerce: 3, services: 2, sante: 1, autres: 1 };
/** Banques, assurances et cabinets comptables soutiennent souvent les clubs : un point de plus que les autres services. */
const SERVICES_PLUS = /^(64\.19|65\.1|66\.2|69\.20)/;
export function sectorPoints(naf: string | null | undefined): number {
  const f = familyOf(naf);
  return f === 'services' && naf && SERVICES_PLUS.test(naf) ? 3 : SECTOR_POINTS[f];
}
export function sizePoints(code: string | null | undefined): number {
  if (!code || code === 'NN' || code === '00') return 0;
  if (code === '01') return 1;
  if (code === '02') return 2;
  if (['03', '11', '12'].includes(code)) return 3;
  if (['21', '22'].includes(code)) return 2;
  return 1;   // 200 salariés et plus : la décision se prend souvent loin du gymnase
}
export function distancePoints(m: number | null | undefined): number {
  if (m == null) return 1;
  if (m <= 1000) return 3;
  if (m <= 3000) return 2;
  if (m <= 5000) return 1;
  return 0;
}
export type AffinityLevel = 1 | 2 | 3;
export const AFFINITY_LABEL: Record<AffinityLevel, string> = { 1: 'faible', 2: 'moyenne', 3: 'forte' };
export function affinity(c: { naf: string | null; size_code: string | null; distance_m: number | null }): { score: number; level: AffinityLevel } {
  const score = sectorPoints(c.naf) + sizePoints(c.size_code) + distancePoints(c.distance_m);
  return { score, level: score >= 8 ? 3 : score >= 5 ? 2 : 1 };
}

// --- Noms et adresses ----------------------------------------------------------------------------------
const SMALL = new Set(['de', 'du', 'des', 'la', 'le', 'les', 'et', 'à', 'au', 'aux', 'en', 'sur', 'sous', 'l', 'd']);
/** Restent en capitales : formes juridiques, sigles courants. */
const FORMS = new Set(['SARL', 'SAS', 'SASU', 'EURL', 'SA', 'SCI', 'SNC', 'SCOP', 'SELARL', 'SCP', 'SCM', 'SELAS', 'GAEC', 'EARL', 'PVC']);
const STREET = new Set(['rue', 'avenue', 'chemin', 'place', 'route', 'boulevard', 'allée', 'allee', 'impasse', 'zone', 'quai', 'cours', 'square', 'résidence',
  'lotissement', 'lieu-dit', 'lieu', 'dit', 'voie', 'passage', 'rond-point', 'za', 'zi', 'zac', 'parc', 'faubourg', 'esplanade', 'promenade', 'sentier', 'hameau']);
/** « MENUISERIE RIVEBELLOISE » → « Menuiserie Rivebelloise » ; « L'ISLE-JOURDAIN » → « L’Isle-Jourdain ». */
export function titleCase(s: string): string {
  const cap = (w: string) => w.charAt(0).toUpperCase() + w.slice(1);
  return s.trim().replace(/\s+/g, ' ').split(' ').map((word, i) => {
    if (FORMS.has(word.toUpperCase()) && word === word.toUpperCase()) return word.toUpperCase();
    if (/\d/.test(word)) return word.toUpperCase();
    return word.toLowerCase().split('-').map(part => part.split(/['’]/).map((p, j, arr) => {
      if (arr.length > 1 && j === 0 && (p === 'l' || p === 'd')) return i === 0 ? cap(p) : p;
      return (i > 0 && SMALL.has(p) && arr.length === 1) ? p : cap(p);
    }).join('’')).join('-');
  }).join(' ');
}
/** L'adresse du registre (« 12 CHEMIN DES TANNEURS 32600 RIVEBELLE ») : « 12 chemin des Tanneurs, Rivebelle ». */
export function prettyAddress(raw: string | null | undefined, city?: string | null): string {
  const s = (raw ?? '').trim();
  if (!s) return city ? titleCase(city) : '';
  const m = /^(.*?)\s*(\d{5})\s+(.*)$/.exec(s);
  const street = m ? m[1]! : s, town = city ?? (m ? m[3]! : '');
  const nice = titleCase(street).split(' ').map((w, i) => (i <= 3 && STREET.has(w.toLowerCase()) ? w.toLowerCase() : w)).join(' ');
  return [nice, town ? titleCase(town) : ''].filter(Boolean).join(', ');
}
/** La rue seule, pour la carte « Qui connaît ? » (« 12 chemin des Tanneurs, Rivebelle » → « chemin des Tanneurs »). */
export function streetOf(address: string | null | undefined): string {
  const first = (address ?? '').split(',')[0]!.trim();
  return first.replace(/^\d+\s*(bis|ter|quater|[a-z])?\s+/i, '').trim();
}
/** Le monogramme d'une entreprise : « MR » pour Menuiserie Rivebelloise. */
export function initials(name: string): string {
  const words = name.replace(/[’']/g, ' ').split(/[\s-]+/).filter(w => w && !SMALL.has(w.toLowerCase()) && !FORMS.has(w.toUpperCase()));
  return (words.length ? words : [name]).slice(0, 2).map(w => w.charAt(0).toUpperCase()).join('') || '·';
}
const PALETTE = ['#8A5A2B', '#334155', '#0E7490', '#5B2A86', '#B45309', '#7A1F3D', '#166534', '#0F766E', '#1D4ED8', '#9A3412', '#4D7C0F', '#475569'];
/** Une couleur stable par entreprise (la même à chaque affichage). */
export function colorOf(key: string): string {
  let h = 0;
  for (const ch of key) h = (h * 31 + ch.codePointAt(0)!) >>> 0;
  return PALETTE[h % PALETTE.length]!;
}

// --- Une réponse de l'API (route /near_point) ----------------------------------------------------------
export interface RegistryRow {
  siret: string; name: string; naf: string; naf_label: string | null; size_code: string | null;
  address: string; lat: number; lon: number; distance_m: number;
}
export type SkipReason = 'ferme' | 'non_diffusible' | 'personne' | 'public' | 'evin' | 'loin' | 'incomplet';
interface ApiEtab {
  siret?: string; etat_administratif?: string; statut_diffusion_etablissement?: string; activite_principale?: string | null;
  tranche_effectif_salarie?: string | null; adresse?: string | null; code_postal?: string | null; libelle_commune?: string | null;
  latitude?: string | number | null; longitude?: string | number | null; liste_enseignes?: string[] | null; nom_commercial?: string | null;
}
interface ApiUnit {
  nom_complet?: string; nom_raison_sociale?: string | null; etat_administratif?: string; statut_diffusion?: string; nature_juridique?: string | null;
  activite_principale?: string | null; section_activite_principale?: string | null; complements?: { est_entrepreneur_individuel?: boolean; est_association?: boolean } | null;
  matching_etablissements?: ApiEtab[];
}
export const SIZE_CODES = new Set(Object.keys(SIZE_LABEL));

/** Le nom à garder : l'enseigne, sinon le nom commercial, sinon la dénomination ; jamais le nom d'une personne. */
export function companyName(u: ApiUnit, e: ApiEtab): string | null {
  const brand = e.liste_enseignes?.find(x => typeof x === 'string' && x.trim()) ?? (e.nom_commercial?.trim() || null);
  const person = u.nature_juridique === '1000' || u.complements?.est_entrepreneur_individuel === true;
  const raw = brand ?? (person ? null : (u.nom_raison_sociale?.trim() || u.nom_complet?.trim() || null));
  if (!raw) return null;
  return titleCase(raw.replace(/\s*\((?:[^)]*)\)\s*$/, '')).slice(0, 120);
}

export function parseNearPoint(json: unknown, gym: Point, radiusKm: number, labels: Record<string, string>): { rows: RegistryRow[]; skipped: Partial<Record<SkipReason, number>> } {
  const rows: RegistryRow[] = [];
  const skipped: Partial<Record<SkipReason, number>> = {};
  const skip = (r: SkipReason) => { skipped[r] = (skipped[r] ?? 0) + 1; };
  const results = (json as { results?: ApiUnit[] } | null)?.results;
  if (!Array.isArray(results)) return { rows, skipped };
  for (const u of results) {
    const nature = u.nature_juridique ?? '';
    for (const e of u.matching_etablissements ?? []) {
      if (u.etat_administratif !== 'A' || e.etat_administratif !== 'A') { skip('ferme'); continue; }
      if (u.statut_diffusion !== 'O' || e.statut_diffusion_etablissement !== 'O') { skip('non_diffusible'); continue; }
      if (/^[79]/.test(nature) || u.complements?.est_association || ['O', 'T', 'U'].includes(u.section_activite_principale ?? '')) { skip('public'); continue; }
      const naf = isNaf(e.activite_principale) ? e.activite_principale : isNaf(u.activite_principale) ? u.activite_principale : null;
      const lat = Number(e.latitude), lon = Number(e.longitude);
      if (!naf || !/^[0-9]{14}$/.test(e.siret ?? '') || !Number.isFinite(lat) || !Number.isFinite(lon) || (lat === 0 && lon === 0)) { skip('incomplet'); continue; }
      if (isEvin(naf)) { skip('evin'); continue; }
      const name = companyName(u, e);
      if (!name) { skip('personne'); continue; }
      const d = distanceM(gym, { lat, lon });
      if (d > radiusKm * 1000) { skip('loin'); continue; }
      const size = e.tranche_effectif_salarie && SIZE_CODES.has(e.tranche_effectif_salarie) ? e.tranche_effectif_salarie : null;
      rows.push({ siret: e.siret!, name, naf, naf_label: labels[naf] ?? null, size_code: size,
        address: prettyAddress(e.adresse, e.libelle_commune).slice(0, 200), lat, lon, distance_m: d });
    }
  }
  return { rows, skipped };
}
